import { render } from "preact";
import { type Signal, signal } from "@preact/signals";
import type { ClayKind } from "../clay.ts";
import { faceUrl } from "../face.ts";
import { loadRecords, type RequestRecord } from "../records.ts";
import { accessTo, challengeFor, cleared, mastered, progressSummary } from "../progression.ts";
import { recordsPersistent } from "../records.ts";
import type { RequestDef } from "../requests.ts";
import type { TargetDef } from "../targets.ts";
import { LetterPaper } from "./letter.tsx";
import { Icon } from "./icon.tsx";
import styles from "./board.module.css";

/** Each letter's picture of what it asks for (an image URL by request id), drawn by the game. */
export const letterThumbs = signal<ReadonlyMap<string, string>>(new Map());

/** Holes in each starting clay: requests are grouped into levels by them. */
const HOLES: Record<ClayKind, number> = {
  blob: 0,
  donut: 1,
  pretzel: 2,
  trefoil: 3,
  quatrefoil: 4,
};

/** What the letters ask of the game. */
export interface BoardActions {
  accept(request: RequestDef): void;
  showShelf(): void;
}

/** The letters, opened and closed by the game. */
export interface Board {
  /**
   * Shows the list; closable when there is somewhere to go back to, `onClose` taking the
   * player there.
   */
  open(closable: boolean, onClose?: () => void): void;
  /** Opens the list again as it was last opened (back from the shelf, say). */
  reopen(): void;
  hide(): void;
  /**
   * The way back, as the buttons go: from a letter to the list, or the list closed (null while
   * it is not shown).
   */
  readonly wayBack: (() => void) | null;
}

/** Requests of one level: those starting from clay with this many holes. */
export interface Level {
  holes: number;
  requests: RequestDef[];
}

/** The requests grouped into levels, fewest holes first. */
export function levelsOf(requests: RequestDef[], targets: TargetDef[]): Level[] {
  const byHoles = new Map<number, RequestDef[]>();
  for (const r of requests) {
    const holes = HOLES[targets.find((t) => t.id === r.target)!.clay];
    byHoles.set(holes, [...(byHoles.get(holes) ?? []), r]);
  }
  return [...byHoles].sort(([a], [b]) => a - b).map(([holes, rs]) => ({ holes, requests: rs }));
}

/** A level's heading, as the letters and the ledger show it. */
export const levelName = (holes: number) =>
  `レベル${holes}（${holes === 0 ? "穴なし" : `${holes}穴`}）`;

/** The letters from customers: a list by level, and a letter opened from it to take on. */
export function mountBoard(
  root: HTMLElement,
  requests: RequestDef[],
  targets: TargetDef[],
  actions: BoardActions,
): Board {
  const open = signal(false), closable = signal(false);
  const chosen = signal<RequestDef | null>(null);
  /** What has been delivered, read again each time the letters are opened. */
  const records = signal<ReadonlyMap<string, RequestRecord>>(new Map());
  const ready = signal(false);
  let opening = 0;
  let onClose: (() => void) | undefined;
  const close = () => {
    open.value = false;
    onClose?.();
  };

  const levels = levelsOf(requests, targets);

  render(
    <BoardScreen
      open={open}
      closable={closable}
      chosen={chosen}
      levels={levels}
      records={records}
      ready={ready}
      accept={(r) => {
        if (!open.value || !ready.value || !accessTo(r.id, records.value).available) return;
        open.value = false;
        actions.accept(r);
      }}
      close={close}
      showShelf={() => actions.showShelf()}
    />,
    root,
  );

  return {
    open(canClose, back) {
      closable.value = canClose;
      onClose = back;
      this.reopen();
    },
    reopen() {
      chosen.value = null;
      open.value = true;
      ready.value = false;
      const version = ++opening;
      loadRecords().then((r) => {
        if (version !== opening) return;
        records.value = r;
        ready.value = true;
      });
    },
    hide() {
      open.value = false;
    },
    get wayBack() {
      if (!open.value) return null;
      if (chosen.value) return () => chosen.value = null;
      // Not to be closed, the list stays as it is.
      return closable.value ? close : () => {};
    },
  };
}

function BoardScreen(
  { open, closable, chosen, levels, records, ready, accept, close, showShelf }: {
    open: Signal<boolean>;
    ready: Signal<boolean>;
    closable: Signal<boolean>;
    chosen: Signal<RequestDef | null>;
    levels: Level[];
    records: Signal<ReadonlyMap<string, RequestRecord>>;
    accept: (r: RequestDef) => void;
    close: () => void;
    showShelf: () => void;
  },
) {
  if (!open.value) return null;
  const letter = chosen.value;
  return (
    <div class={styles.board}>
      <div class={styles.card}>
        <h2 class={styles.heading}>依頼の手紙</h2>
        {!recordsPersistent && (
          <p class={styles.notice}>
            この端末に進み具合を保存できません。今のタブでは続けられますが、再読み込みやタブを閉じると失われます。
          </p>
        )}
        {letter
          ? (
            <div class={styles.letter}>
              <LetterPaper
                body={letter.letter}
                from={letter.client}
                face={letter.face}
                paper={letter.paper}
                postscript={letter.postscript}
              />
              {challengeFor(letter.id) && <ChallengeRules id={letter.id} />}
              <div class={styles.wishes}>
                <p class={styles.wish}>こだわり: {letter.wish.label}</p>
                {letter.atmosphere && (
                  <p class={`${styles.wish} ${styles.atmosphere}`}>
                    雰囲気: {letter.atmosphere.label}
                  </p>
                )}
              </div>
              <div class={`row ${styles.actions}`}>
                <button type="button" class="reset" onClick={() => chosen.value = null}>
                  もどる
                </button>
                <button type="button" class="submit" onClick={() => accept(letter)}>
                  引き受ける
                </button>
              </div>
            </div>
          )
          : (
            <div class={styles.list}>
              <p class={styles.progress} role="status">
                {ready.value ? progressSummary(records.value) : "依頼帳を読んでいます…"}
              </p>
              <p class={styles.progress}>
                通常依頼は点数を問わず納品で進みます。お題はB以上。自由にこねるはいつでも遊べます。
              </p>
              {levels.map(({ holes, requests }) => [
                <p key={`level-${holes}`} class={styles.level}>{levelName(holes)}</p>,
                ...requests.map((r) => {
                  const thumb = letterThumbs.value.get(r.id);
                  const record = records.value.get(r.id);
                  const rank = record?.bestRank;
                  const access = accessTo(r.id, records.value);
                  const challenge = challengeFor(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      class={styles.item}
                      data-locked={!access.available ? "true" : undefined}
                      disabled={!ready.value || !access.available}
                      onClick={() => {
                        if (ready.value && access.available) chosen.value = r;
                      }}
                    >
                      {
                        // The best delivery's rank, stamped in the corner once there is one.
                        access.available && rank && (
                          <span
                            class={styles.itemRank}
                            data-rank={rank}
                            title={`最高ランク ${rank}`}
                          >
                            {rank}
                          </span>
                        )
                      }
                      {access.available && (
                        <img class={styles.itemFace} src={faceUrl(r.face)} alt="" />
                      )}
                      <span class={styles.thumb}>
                        {thumb && <img src={thumb} alt="" />}
                        {!access.available && (
                          <span class={styles.lockBadge} aria-hidden="true">
                            <Icon name="lock" />
                          </span>
                        )}
                      </span>
                      {access.available
                        ? (
                          <>
                            {challenge && <span class={styles.kind}>お題</span>}
                            <span class={styles.itemTitle}>{r.title}</span>
                            {challenge && cleared(record) && (
                              <span class={styles.kind}>お題達成</span>
                            )}
                            {challenge && mastered(r.id, record) && (
                              <span class={styles.kind}>腕試し達成</span>
                            )}
                            <span class={styles.itemClient}>{r.client}より</span>
                            <span class={styles.itemWish} title="こだわり">{r.wish.label}</span>
                          </>
                        )
                        : (
                          <span class={styles.lock}>
                            <span class={styles.lockLabel}>未解放</span>
                            {access.reason}
                          </span>
                        )}
                    </button>
                  );
                }),
              ])}
            </div>
          )}
        {
          // A letter open has its own way back (もどる) and on (引き受ける).
          !letter && (
            <div class={`row ${styles.actions}`}>
              <button type="button" class="reset" onClick={showShelf}>棚を見る</button>
              {closable.value && (
                <button type="button" class="reset" onClick={close}>閉じる</button>
              )}
            </div>
          )
        }
      </div>
    </div>
  );
}

/** Conditions stay visible before accepting a challenge. */
function ChallengeRules({ id }: { id: string }) {
  const challenge = challengeFor(id)!;
  return (
    <div class={styles.challenge}>
      <b>お題「{challenge.title}」</b>
      <p>{challenge.lesson}</p>
      <p>達成: B以上（70点）で納品。手数・道具は自由。こね直しも何度でも。</p>
      <p>
        任意の腕試し: 同じ作品でA以上（100点）・{challenge
          .maxStrokes}手以内。解放条件には入りません。
      </p>
    </div>
  );
}
